import { FC, useCallback, useContext } from 'react';
import { Button, Dropdown, Modal, Result, Space, Spin, Statistic, Table, Tooltip, message } from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';

import { useBackgroundNavigate } from '@shared/hooks';
import { DateFormat } from '@shared/constants';
import { CurrencySymbols } from '@shared/utils';
import { DetailCard, DetailCol, DetailDescriptions, DetailHeader, DetailPage, DetailRow } from '@shared/styled/detail';
import { SettingsContext } from '@modules/settings';
import { useAuth } from '@modules/me';
import { useGetUserById, useGetDiscountStats, useUserRelatedSections, useUserAddress } from '../hooks';
import { UsersService } from '../services';

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  warehouseman: 'Anbardar',
  'back-office': 'Back office',
  partner: 'Partner',
  user: 'Müştəri',
};

export const UserDetails: FC<{ id: string }> = ({ id }) => {
  const navigate = useBackgroundNavigate();
  const { can } = useAuth();
  const settings = useContext(SettingsContext);
  const { data, isLoading, error, refetch } = useGetUserById(id);
  const stats = useGetDiscountStats(id);
  const { openOrders, openDeclarations, openCurrentMonthDeclarations, openCouriers, openTransactions, openEmail, openSMS, openCustomsDeclarations, openTickets, openTicketsForm, openWhatsapp } =
    useUserRelatedSections(id, data);
  const [copyAddress] = useUserAddress(data);

  const onDelete = useCallback(() => {
    Modal.confirm({
      title: 'İstifadəçini sil',
      content: 'Bu əməliyyat geri alına bilməz. Davam etmək istəyirsiniz?',
      okType: 'danger',
      onOk: async () => {
        const result = await UsersService.deleteUser([id]);
        if (result.status === 200) {
          message.success('İstifadəçi silindi');
          navigate('/users', { withBackground: false });
        } else {
          message.error(result.data as string);
        }
      },
    });
  }, [id, navigate]);

  const onToggleBlock = useCallback(() => {
    if (!data) return;
    Modal.confirm({
      title: 'Diqqət',
      content: data.isBlocked ? 'İstifadəçini blokdan çıxarmağa əminsinizmi?' : 'İstifadəçini bloklamağa əminsinizmi?',
      onOk: async () => {
        const result = await UsersService.blockUsers([id], !data.isBlocked);
        if (result.status === 200) refetch();
        else message.error(result.data as string);
      },
    });
  }, [data, id, refetch]);

  const onUpdateRole = useCallback(
    (role: string) => {
      Modal.confirm({
        title: 'Diqqət',
        content: 'Səlahiyyəti dəyişməyə əminsinizmi?',
        onOk: async () => {
          const result = await UsersService.updateUserRole([id], role);
          if (result.status === 200) refetch();
          else message.error(result.data as string);
        },
      });
    },
    [id, refetch],
  );

  const onDeleteDiscount = useCallback(
    (discountId: number) => {
      Modal.confirm({
        title: 'Endirimi sil',
        content: 'Endirimi silməyə əminsinizmi?',
        okType: 'danger',
        onOk: async () => {
          const result = await UsersService.removeDiscount(discountId);
          if (result.status === 200) refetch();
          else message.error(result.data as string);
        },
      });
    },
    [refetch],
  );

  if (isLoading) {
    return (
      <DetailPage>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      </DetailPage>
    );
  }

  if (error || !data) {
    return (
      <DetailPage>
        <Result status="500" title="Xəta baş verdi" subTitle={(error as Error)?.message || 'Məlumatlar əldə edilə bilmədi'} />
      </DetailPage>
    );
  }

  const orderCounts = (
    <div>
      <div>
        <b>Ümumi say: </b> {data.counts.orders.all}
      </div>
      <div>
        <b>Təhvil verilmiş: </b> {data.counts.orders.handedOver}
      </div>
    </div>
  );

  const declarationCounts = (
    <div>
      <div>
        <b>Ümumi say: </b> {data.counts.declarations.all}
      </div>
      <div>
        <b>Təhvil verilmiş: </b> {data.counts.declarations.handedOver}
      </div>
    </div>
  );

  const courierCounts = (
    <div>
      <div>
        <b>Ümumi say: </b> {data.counts.couriers.all}
      </div>
      <div>
        <b>Təhvil verilmiş: </b> {data.counts.couriers.handedOver}
      </div>
    </div>
  );

  const transactionCounts = (
    <div>
      <div>
        <b>Mədaxil sayı: </b> {data.counts.transactions.income}
      </div>
      <div>
        <b>Məxaric sayı: </b> {data.counts.transactions.outcome}
      </div>
    </div>
  );

  const title = (
    <Space size={8}>
      <Icons.LeftCircleOutlined style={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
      <span>#{data.id}</span>
    </Space>
  );

  const roleMenuItems = [
    { key: 'client', label: 'Müştəri', onClick: () => onUpdateRole('client') },
    { key: 'admin', label: 'Admin', onClick: () => onUpdateRole('admin') },
    { key: 'warehouseman', label: 'Anbardar', onClick: () => onUpdateRole('warehouseman') },
    { key: 'back-office', label: 'Back Office', onClick: () => onUpdateRole('back-office') },
    { key: 'partner', label: 'Partner', onClick: () => onUpdateRole('partner') },
  ];

  const extra = (
    <Space size={8}>
      {data.role !== 'user' && can('changeuserpermissions') && (
        <Tooltip title="İcazələr">
          <Button type="link" icon={<Icons.SettingOutlined />} onClick={() => navigate(`/users/${id}/permissions`, { withBackground: true })} />
        </Tooltip>
      )}
      <Dropdown menu={{ items: roleMenuItems }} trigger={['click']}>
        <Tooltip title="Səlahiyyəti dəyiş">
          <Button type="link" icon={<Icons.AppstoreAddOutlined />} />
        </Tooltip>
      </Dropdown>
      <Tooltip title={data.isBlocked ? 'Blokdan çıxar' : 'Blokla'}>
        <Button type="link" icon={data.isBlocked ? <Icons.UnlockOutlined /> : <Icons.LockOutlined />} onClick={onToggleBlock} />
      </Tooltip>
      <Tooltip title="Düzəliş et">
        <Button type="link" icon={<Icons.EditOutlined />} onClick={() => navigate(`/users/${id}/update`, { withBackground: true })} />
      </Tooltip>
      <Tooltip title="Sil">
        <Button type="link" danger icon={<Icons.DeleteOutlined />} onClick={onDelete} />
      </Tooltip>
    </Space>
  );

  const navButtons = (
    <Space wrap size={8}>
      <Tooltip placement="bottom" title={orderCounts}>
        <Button ghost type="primary" onClick={openOrders}>
          Sifarişlər
        </Button>
      </Tooltip>
      <Tooltip placement="bottom" title={declarationCounts}>
        <Space.Compact>
          <Button ghost type="primary" onClick={openDeclarations}>
            Bağlamalar
          </Button>
          <Button ghost type="primary" onClick={openCurrentMonthDeclarations}>
            Cari ay
          </Button>
        </Space.Compact>
      </Tooltip>
      <Tooltip placement="bottom" title={courierCounts}>
        <Button ghost type="primary" onClick={openCouriers}>
          Kuryerlər
        </Button>
      </Tooltip>
      <Tooltip placement="bottom" title={transactionCounts}>
        <Button ghost type="primary" onClick={openTransactions}>
          Balans əməliyyatları
        </Button>
      </Tooltip>
      <Space.Compact>
        <Button ghost type="primary" onClick={openTickets}>
          Müraciətlər
        </Button>
        <Button ghost type="primary" onClick={openTicketsForm} icon={<Icons.PlusCircleOutlined />} />
      </Space.Compact>
      <Button ghost type="primary" onClick={openEmail}>
        Emaillər
      </Button>
      <Button ghost type="primary" onClick={openSMS}>
        SMSlər
      </Button>
      <Button ghost type="primary" onClick={openCustomsDeclarations}>
        DGK məlumatları
      </Button>
      {can('user_discount') && (
        <Button ghost type="primary" onClick={() => navigate(`/users/${id}/create-discount`, { withBackground: true })}>
          Endirim tətbiq et
        </Button>
      )}
    </Space>
  );

  return (
    <DetailPage>
      <DetailRow>
        <DetailCol xs={24}>
          <DetailHeader title={title} subTitle={data.fullName} extra={extra} />
        </DetailCol>

        <DetailCol xs={24}>{navButtons}</DetailCol>

        <DetailCol xs={24}>
          <DetailRow>
            <DetailCol xs={24} lg={4}>
              <DetailCard>
                <Statistic title="Balans" value={data.balance.usd} prefix={CurrencySymbols.USD} precision={2} />
              </DetailCard>
            </DetailCol>
            <DetailCol xs={24} lg={4}>
              <DetailCard>
                <Statistic title="Balans" value={data.balance.try} prefix={CurrencySymbols.TRY} precision={2} />
              </DetailCard>
            </DetailCol>
            <DetailCol xs={24} lg={4}>
              <DetailCard>
                <Statistic title="Borc" value={data.debt.try} prefix={CurrencySymbols.TRY} precision={2} />
              </DetailCard>
            </DetailCol>
            <DetailCol xs={24} lg={4}>
              <DetailCard>
                <Statistic title="Cari ay" value={data.spending.currentMonth.usd} prefix={CurrencySymbols.USD} precision={2} />
              </DetailCard>
            </DetailCol>
            <DetailCol xs={24} lg={5}>
              <DetailCard>
                <Statistic title="Cari ay kəşbək" value={data.cashback.currentCashback} prefix={<Icons.PercentageOutlined />} suffix={CurrencySymbols.USD} precision={2} />
              </DetailCard>
            </DetailCol>
          </DetailRow>
        </DetailCol>

        <DetailCol xs={24}>
          {(settings.warehouse || []).map((wh) => (
            <DetailCard key={wh.id} title={`${wh.country_name} ünvan`}>
              {wh.address} <Icons.CopyOutlined style={{ cursor: 'pointer' }} onClick={() => copyAddress(wh.address)} />
            </DetailCard>
          ))}
        </DetailCol>

        <DetailCol xs={24} lg={8}>
          <DetailCard title="Şəxsi məlumatlar">
            <DetailDescriptions>
              <DetailDescriptions.Item label="Ad">
                {data.firstname} {data.lastname}
              </DetailDescriptions.Item>
              <DetailDescriptions.Item label="Təvəllüd">{data.birthDate ? dayjs(data.birthDate).format(DateFormat.DATE_DOT) : '—'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Cinsi">{data.gender === 'male' ? 'Kişi' : 'Qadın'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Ş.V nömrəsi">{data.passport.number || '—'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="FİN kod">{data.passport.secret || '—'}</DetailDescriptions.Item>
            </DetailDescriptions>
          </DetailCard>
        </DetailCol>

        <DetailCol xs={24} lg={8}>
          <DetailCard title="Sistem məlumatları">
            <DetailDescriptions>
              <DetailDescriptions.Item label="Səlahiyyət">{roleLabels[data.role] || 'Müştəri'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Admin filialı">{data.adminBranchName || '—'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Admin şirkəti">{data.adminCompanyName || '—'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="SMS xidməti">{data.sendSms ? 'Aktivdir' : 'Aktiv deyil'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Email xidməti">{data.sendEmail ? 'Aktivdir' : 'Aktiv deyil'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Bloklanma statusu">{data.isBlocked ? 'Bloklanıb' : 'Aktivdir'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Yaradılma tarixi">{dayjs(data.createdAt).format(DateFormat.DATE_TIME_DOT)}</DetailDescriptions.Item>
            </DetailDescriptions>
          </DetailCard>
        </DetailCol>

        <DetailCol xs={24} lg={8}>
          <DetailCard title="Əlaqə məlumatları">
            <DetailDescriptions>
              <DetailDescriptions.Item label="Telefon">
                <Space size={8}>
                  <span>{data.phoneNumber || '—'}</span>
                  {data.phoneNumber && <Icons.WhatsAppOutlined style={{ color: '#25d366', cursor: 'pointer' }} onClick={openWhatsapp} />}
                </Space>
              </DetailDescriptions.Item>
              <DetailDescriptions.Item label="Email">{data.email}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Ünvan">{data.address || '—'}</DetailDescriptions.Item>
              <DetailDescriptions.Item label="Filial">{data.branch.name || '—'}</DetailDescriptions.Item>
            </DetailDescriptions>
          </DetailCard>
        </DetailCol>

        <DetailCol xs={24} lg={8}>
          <DetailCard title="Kəşbək məlumatları">
            <DetailDescriptions>
              <DetailDescriptions.Item label="Cari ay kəşbək">
                {data.cashback.currentCashback} {CurrencySymbols.USD}
              </DetailDescriptions.Item>
              <DetailDescriptions.Item label="Təsdiqlənmiş kəşbək">
                {data.cashback.totalCashbackApproved} {CurrencySymbols.USD}
              </DetailDescriptions.Item>
              <DetailDescriptions.Item label="Gözlənilən kəşbək">
                {data.cashback.totalCashbackPending} {CurrencySymbols.USD}
              </DetailDescriptions.Item>
            </DetailDescriptions>
          </DetailCard>
        </DetailCol>

        <DetailCol lg={24}>
          <DetailRow gutter={[24, 24]}>
            <DetailCol xs={24} lg={12}>
              <DetailCard title="Endirimlər">
                {data.discounts.length ? (
                  <Table rowKey="id" size="small" bordered pagination={false} dataSource={data.discounts}>
                    <Table.Column dataIndex="id" title="Kod" width={60} />
                    <Table.Column dataIndex="countryName" title="Ölkə" />
                    <Table.Column dataIndex="causerName" title="Kim tərəfindən" />
                    <Table.Column dataIndex="discount" title="Endirim (%)" />
                    <Table.Column dataIndex="discountDate" title="Tarix" />
                    <Table.Column dataIndex="descr" title="Qeyd" />
                    <Table.Column
                      key="actions"
                      width={60}
                      render={(_: any, row: any) => (
                        <Button size="small" danger onClick={() => onDeleteDiscount(row.id)}>
                          Sil
                        </Button>
                      )}
                    />
                  </Table>
                ) : (
                  <span style={{ color: '#999' }}>Endirim yoxdur...</span>
                )}
              </DetailCard>
            </DetailCol>

            <DetailCol xs={24} lg={12}>
              <DetailCard title="Statistika">
                {stats.data?.length ? (
                  <Table rowKey="date" size="small" bordered pagination={false} dataSource={stats.data} loading={stats.isLoading}>
                    <Table.Column dataIndex="monthName" title="Ay" />
                    <Table.Column dataIndex="weight" title="Çəki" />
                    <Table.Column dataIndex="quantity" title="Say" />
                    <Table.Column dataIndex="deliveryPrice" title="Çatdırılma qiyməti" />
                  </Table>
                ) : (
                  <span style={{ color: '#999' }}>Bağlaması yoxdur...</span>
                )}
              </DetailCard>
            </DetailCol>
          </DetailRow>
        </DetailCol>
      </DetailRow>
    </DetailPage>
  );
};
